import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { findPlanAnchorObservationTarget } from './planAnchor';

/**
 * True while the plan card's top edge is scrolled out of the transcript —
 * observes the card's hairline top sentinel, so any clipping at the top (or
 * the card leaving entirely) counts, while a card peeking in from the bottom
 * does not.
 *
 * Keyed on the anchor id, NOT the plan object: the plan is re-derived on every
 * streamed chunk, so re-subscribing on it would blink the summary off several
 * times a second.
 *
 * The timeline is also rebuilt on every chunk, which can mount the anchor
 * after the first lookup or swap its node without an observer callback firing
 * — the MutationObserver on the scroller re-resolves in both cases, since a
 * detached target reports nothing and would otherwise leave a dead observer.
 *
 * Defaults to false so a missing observer leaves the summary hidden rather than
 * pinned over a visible card.
 */
export const usePlanAnchorOffScreen = (
  anchorId: string | null,
  scrollRef: RefObject<HTMLElement>
): boolean => {
  const [offScreen, setOffScreen] = useState(false);
  const [anchorGeneration, setAnchorGeneration] = useState(0);
  const reresolveAnchor = useCallback(() => {
    setAnchorGeneration((generation) => generation + 1);
  }, []);

  useEffect(() => {
    setOffScreen(false);
    if (!anchorId) return;

    const scroller = scrollRef.current;
    if (!scroller) return;

    const target = findPlanAnchorObservationTarget(scroller, anchorId);
    let observer: IntersectionObserver | undefined;
    if (target && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          if (!entry.target.isConnected) {
            reresolveAnchor();
            return;
          }
          setOffScreen(!entry.isIntersecting);
        },
        { root: scroller }
      );
      observer.observe(target);
    }

    let mutationObserver: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        if (findPlanAnchorObservationTarget(scroller, anchorId) !== target) {
          reresolveAnchor();
        }
      });
      mutationObserver.observe(scroller, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [anchorId, anchorGeneration, reresolveAnchor, scrollRef]);

  return offScreen;
};
